import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportPaymentsMinusRefundsByCustomerStateData,
  TReportPaymentsMinusRefundsByCustomerStateRes,
  TReportPaymentsMinusRefundsByCustomerStateParams,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import { useCountryOptions } from '@hooks/useCountryOptions';

import { ReportFilteringSettings } from '../ReportFilteringSettings';
import { useProducts } from '@hooks/useProducts.ts';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.dataKey}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const PaymentsMinusRefundsByCustomerState: FC = () => {
  // Data state
  const [data, setData] = useState<
    TReportPaymentsMinusRefundsByCustomerStateData[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  // Country options
  const { countryOptions } = useCountryOptions();
  const { products } = useProducts();

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableProducts: true,
    enableCountries: true,
    enableComparisons: true,
  });

  // Use custom hook for table columns generation - adapted for states
  const { columns, tableData } = useReportTableColumns({
    data,
    selectedItems: [],
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.states[key] || 0,
    getPreviousPeriodField: (item, key) => item.states_previous?.[key],
    getPreviousYearField: (item, key) => item.states_previous_year?.[key],
    itemsField: 'states',
    valueFormatter: (_, value: number) => `$${value.toLocaleString()}`,
    includeTotalsRow: true,
  });

  // Get all state keys from data for display
  const getStateKeys = (): string[] => {
    const stateSet = new Set<string>();

    data.forEach((item) => {
      Object.keys(item.states).forEach((state) => stateSet.add(state));
    });

    return Array.from(stateSet).sort();
  };

  const stateKeys = getStateKeys();

  const getValidProductIds = (): number[] => {
    return filters.selectedProducts
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0);
  };

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems: stateKeys,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.states[key] || 0,
    getPreviousPeriodField: (item, key) => item.states_previous?.[key],
    getPreviousYearField: (item, key) => item.states_previous_year?.[key],
  });

  const handleRunReport = async () => {
    if (!filters.selectedCountries || filters.selectedCountries.length === 0) {
      toast.error('Please select a country');

      return;
    }

    if (!filters.selectedProducts || filters.selectedProducts.length === 0) {
      toast.error('Please select at least one product');

      return;
    }

    const selectedCountry = Array.isArray(filters.selectedCountries)
      ? filters.selectedCountries[0]
      : filters.selectedCountries;

    try {
      setIsLoading(true);

      const productIds = getValidProductIds();

      if (productIds.length === 0) {
        toast.error('Please select at least one valid product');

        return;
      }

      const params: TReportPaymentsMinusRefundsByCustomerStateParams = {
        country: selectedCountry,
        start_date: filters.startDate
          ? dayjs(filters.startDate).format('YYYY-MM-DD')
          : null,
        end_date: filters.endDate
          ? dayjs(filters.endDate).format('YYYY-MM-DD')
          : null,
        group: filters.selectedGroupBy.toLowerCase() as ReportGroupBy,
        product_ids: productIds,
        compare_to_previous_period: filters.compareToPreviousPeriod,
        compare_to_same_period_previous_year:
          filters.compareToSamePeriodPreviousYear,
      };

      const response =
        await axios.post<TReportPaymentsMinusRefundsByCustomerStateRes>(
          '/reports/payments-minus-refunds-by-customer-state/',
          params
        );

      setData(response.data.data);
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    if (!filters.selectedCountries || filters.selectedCountries.length === 0) {
      toast.error('Please run the report first');

      return;
    }

    if (!filters.selectedProducts || filters.selectedProducts.length === 0) {
      toast.error('Please select at least one product');

      return;
    }

    const selectedCountry = Array.isArray(filters.selectedCountries)
      ? filters.selectedCountries[0]
      : filters.selectedCountries;

    try {
      const productIds = getValidProductIds();

      if (productIds.length === 0) {
        toast.error('Please select at least one valid product');

        return;
      }

      const params: TReportPaymentsMinusRefundsByCustomerStateParams = {
        country: selectedCountry,
        start_date: filters.startDate
          ? dayjs(filters.startDate).format('YYYY-MM-DD')
          : null,
        end_date: filters.endDate
          ? dayjs(filters.endDate).format('YYYY-MM-DD')
          : null,
        group: filters.selectedGroupBy.toLowerCase() as ReportGroupBy,
        product_ids: productIds,
        compare_to_previous_period: filters.compareToPreviousPeriod,
        compare_to_same_period_previous_year:
          filters.compareToSamePeriodPreviousYear,
      };

      const response = await axios.post(
        '/reports/payments-minus-refunds-by-customer-state/export/?format=csv',
        params,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute(
        'download',
        `payments_minus_refunds_by_customer_state_${new Date().getTime()}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      toast.error('Error downloading CSV');
      console.error('Error downloading CSV:', error);
    }
  };

  useEffect(() => {
    if (filters.selectedCountries && filters.selectedCountries.length > 0) {
      handleRunReport();
    }
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Payments Minus Refunds by Customer State
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          countryOptions={countryOptions}
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isCountriesMulti={false}
          isLoading={isLoading || filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          productOptions={products}
          selectedCountries={filters.selectedCountries}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedProducts={filters.selectedProducts}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          showCountriesFilter={true}
          showProductsFilter={true}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onCountriesChange={filters.setSelectedCountries}
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onProductsChange={filters.setSelectedProducts}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data lines */}
              {stateKeys.map((state, index) => {
                return (
                  <Line
                    key={state}
                    activeDot={{ r: 6 }}
                    dataKey={state}
                    dot={{ fill: getChartColor(index), r: 4 }}
                    name={state}
                    stroke={getChartColor(index)}
                    strokeWidth={2}
                    type="monotone"
                  />
                );
              })}

              {/* Previous period lines */}
              {filters.compareToPreviousPeriod &&
                stateKeys.map((state, index) => {
                  return (
                    <Line
                      key={`${state}_previous_period`}
                      activeDot={{ r: 4 }}
                      dataKey={`${state}_previous_period`}
                      dot={{
                        fill: getChartColor(index),
                        r: 3,
                      }}
                      name={`${state} (Previous Period)`}
                      stroke={getChartColor(index)}
                      strokeDasharray="5 5"
                      strokeWidth={1.5}
                      type="monotone"
                    />
                  );
                })}

              {/* Previous year lines */}
              {filters.compareToSamePeriodPreviousYear &&
                stateKeys.map((state, index) => {
                  return (
                    <Line
                      key={`${state}_previous_year`}
                      activeDot={{ r: 4 }}
                      dataKey={`${state}_previous_year`}
                      dot={{
                        fill: getChartColor(index),
                        r: 3,
                      }}
                      name={`${state} (Previous Year)`}
                      stroke={getChartColor(index)}
                      strokeDasharray="2 2"
                      strokeWidth={1.5}
                      type="monotone"
                    />
                  );
                })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              disabled={isLoading || data.length === 0}
              variant="outline"
              onClick={handleDownloadCSV}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
